import { ReactElement } from 'react';
import classNames from 'classnames';
import { DialogPanel, DialogTitle } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faXmark,
  faCrown,
  faInfinity,
  faUserGroup,
  faCloudArrowUp,
  faWandMagicSparkles,
  faArrowRight,
} from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from 'core/i18n/navigation';
import { appRoutes } from 'config/routing';
import { closeModal } from 'xstate/actors/ModalActor';
import { useTranslations } from 'next-intl';

// Benefits mirror the "Pro" plan on /pricing/individual, led by the ones the
// free tier caps (boxes/collections). This modal only *pitches* Pro — the CTA
// takes the user to the subscription screen to actually choose a plan.
const FEATURES: {
  icon: IconProp;
  iconClass: string;
  key: 'unlimited' | 'share' | 'storage' | 'ai';
}[] = [
  { icon: faInfinity, iconClass: 'bg-mf-blue', key: 'unlimited' },
  { icon: faUserGroup, iconClass: 'bg-mf-green', key: 'share' },
  { icon: faCloudArrowUp, iconClass: 'bg-mf-orange', key: 'storage' },
  { icon: faWandMagicSparkles, iconClass: 'bg-mf-lilac', key: 'ai' },
];

const ProModal = (): ReactElement => {
  const t = useTranslations();
  const router = useRouter();

  const seePlans = () => {
    closeModal();
    router.push(appRoutes.SETTINGS.SUBSCRIPTION);
  };

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <button
            type="button"
            onClick={closeModal}
            aria-label={t('common.close')}
            className={styles.close}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>

          <div className={styles.header}>
            <span className={styles.crownWrap}>
              <FontAwesomeIcon icon={faCrown} className={styles.crown} />
            </span>
            <DialogTitle as="h2" className={styles.title}>
              MindFeed Pro
            </DialogTitle>
            <p className={styles.subtitle}>{t('proModal.subtitle')}</p>

            <ul className={styles.features}>
              {FEATURES.map((feature) => (
                <li key={feature.key} className={styles.feature}>
                  <span
                    className={classNames(
                      styles.featureIcon,
                      feature.iconClass,
                    )}
                  >
                    <FontAwesomeIcon icon={feature.icon} />
                  </span>
                  <span className={styles.featureText}>
                    <span className={styles.featureTitle}>
                      {t(`proModal.${feature.key}.title`)}
                    </span>
                    <span className={styles.featureBody}>
                      {t(`proModal.${feature.key}.body`)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.footer}>
            <button type="button" onClick={seePlans} className={styles.cta}>
              {t('proModal.seePlans')}
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            <button
              type="button"
              onClick={closeModal}
              className={styles.dismiss}
            >
              {t('proModal.notNow')}
            </button>
          </div>
        </DialogPanel>
      </div>
    </div>
  );
};

export default ProModal;

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  inner: 'flex min-h-full items-center justify-center p-4',
  panel: classNames(
    'relative w-full max-w-md overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all',
    'data-closed:opacity-0 data-closed:scale-95 data-enter:duration-200 data-leave:duration-150',
  ),
  close:
    'absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25',
  header: 'bg-mf-blue-800 px-6 pt-6 pb-6 text-white',
  crownWrap:
    'flex h-12 w-12 items-center justify-center rounded-full bg-white/10',
  crown: 'text-xl text-mf-orange',
  title: 'mt-4 text-2xl font-bold',
  subtitle: 'mt-1 text-sm text-white/80',
  features: 'mt-5 space-y-2.5',
  feature: 'flex items-center gap-3 rounded-2xl bg-white/10 px-3 py-3',
  featureIcon:
    'flex-none flex h-9 w-9 items-center justify-center rounded-xl text-white',
  featureText: 'flex flex-col',
  featureTitle: 'text-sm font-semibold',
  featureBody: 'text-xs text-white/70',
  footer: 'px-6 py-5',
  cta: classNames(
    'flex w-full items-center justify-center gap-2 rounded-full bg-mf-blue py-3 text-sm font-semibold text-white',
    'hover:bg-mf-blue-600 focus:outline-none',
  ),
  dismiss:
    'mt-2 w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none',
};
